import { BlockTitle } from "@blocks/Blocks";
import BasicCard from "@components/ArticleCard/BasicCard";
import SplideArrows from "@components/Slider/components/SplideArrows";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { Grid } from "@splidejs/splide-extension-grid";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getFormattedUrlPath } from "@util/helpers";

import "@greglaisph/react-splide/css/core";

type PodcastsProps = {
  /** This is the classname of the component. */
  className?: string;
  /** List of articles to be displayed. */
  articles: SanitizedSectionArticle[];
};

/** The podcasts component in BT SG 60 page.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79113&m=dev
 */
const Podcasts = ({
  className,
  articles,
}: PodcastsProps): React.ReactElement => {
  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  if (articles.length === 0) return <></>;

  const options: Options = {
    pagination: false,
    type: "slide",
    autoplay: false,
    interval: 10000,
    perMove: 1,
    gap: "1rem",
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    perPage: 2,
    fixedWidth: "90%",
    arrows: false,
    breakpoints: {
      768: { fixedWidth: "60%", arrows: true },
      992: { fixedWidth: undefined, perMove: 3, perPage: 3 },
    },
  };

  return (
    <div
      data-testid="podcasts-component"
      className={cn("border-t border-gray-850 pt-3", className)}
    >
      <BlockTitle text="PODCASTS" rootClassName="mb-4" />

      <Splide
        options={options}
        extensions={{ Grid }}
        tag="div"
        hasTrack={false}
        className="relative"
      >
        <SplideArrows className="absolute -top-12 right-0 flex flex-row items-center justify-center gap-2" />

        <SplideTrack>
          {articles.map(
            ({ id, title, kicker, urlPath, media, paywall, sections }) => {
              const defaultImage = {
                directoryName: sections?.[0]?.directoryName || "",
              };
              const link = getFormattedUrlPath(urlPath, [
                { key: "ref", value: "btsg60-podcasts" },
              ]);

              return (
                <SplideSlide key={id}>
                  <BasicCard
                    id={id}
                    title={title}
                    media={media}
                    kicker={kicker?.fields?.[0]?.value}
                    paywall={
                      isSubscriber ? paywall.contentAccess === "1" : undefined
                    }
                    defaultImage={defaultImage}
                    slug={link}
                    variations={{
                      image: {
                        position: "left",
                        width: "w-[80px]",
                        aspectRatio: cn(
                          "aspect-square object-contain object-center"
                        ),
                        filters: [{ w: 320, h: 320, dpr: 1, f: "webp" }],
                        orientation: "square",
                      },
                      content: {
                        width: "w-[calc(100%-80px)]",
                        extraClass: "pl-4",
                      },
                      title: {
                        color: "text-gray-850",
                        size: "text-base",
                        extraClass: cn("font-lct font-semibold leading-tight"),
                      },
                      kicker: {
                        color: "text-gray-850",
                        extraClass: "tracking-[1px]",
                      },
                    }}
                  />
                </SplideSlide>
              );
            }
          )}
        </SplideTrack>
      </Splide>
    </div>
  );
};

export default Podcasts;
